<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { getCRMOrigin } from 'shared/helpers/env';
import ImageAudioVideo from '../../bubble/ImageAudioVideo.vue';

import { formatOrderDate } from '../utils';
import IconRightArrowLong from '../icons/IconRightArrowLong.vue';

import type { IReverseOrderMessage } from './types';
import { computed } from 'vue';

const props = defineProps<{ message: IReverseOrderMessage }>();

const { t } = useI18n();

const { reverseId, applyTime, reverseItemList = [], labels, refundMode, refundTimeline, totalActualReturnQty, totalAmountTxt } = props.message ?? {};

// refundMode = [{
//   icon: 'https://img101.savana.com/0da10966667842dbb7d8c00b94565f27',
//   content: 'Savana Walletxxxx'
// }, {
//   icon: 'https://img101.savana.com/0da10966667842dbb7d8c00b94565f27',
//   content: 'Back Cardxxxxxxxx'
// }];

const { applicationTime, returnMode, returnTimeline, total, view } = labels ?? {};

const showList = computed(() => reverseItemList?.slice(0, reverseItemList.length === 4 ? 4 : 3));

const firstReverseItem = reverseItemList?.[0];

const handleDetail = () => {
  window.open(`${getCRMOrigin()}/refund-detail?reverseId=${reverseId}`);
};

const { coverImage, count, goodsName, skuValues = [] } = firstReverseItem ?? {};

</script>

<template>
  <!-- 描述 -->
  <div class="mb-[10px] flex flex-col gap-[4px]">
    <p v-if="applyTime" class="text-desc">
      <span>{{ applicationTime ?? t('CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.APPLY_TIME') }}: </span>
      <span>{{ formatOrderDate(applyTime) }}</span>
    </p>
    <p v-if="Array.isArray(refundMode) && refundMode.length > 0" class="text-desc">
      <span>{{ returnMode ?? t('CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.RETURN_MODE') }}: </span>
      <span v-for="(item, index) in refundMode" :key="item.content">
        <img v-if="item.icon" class="return-mode-img inline-block h-[16px] ml-[2px]" :src="item.icon">
        {{ item.content }}
        <span v-if="index !== refundMode.length - 1" class="mr-[4px] ml-[4px]">/</span>
      </span>
    </p>
    <p v-if="refundTimeline" class="text-desc">
      <span>{{ returnTimeline ?? t('CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.RETURN_TIMELINE') }}: </span>
      <span>{{ refundTimeline }}</span>
    </p>
  </div>
  <!-- 单卡片 -->
  <div v-if="reverseItemList.length === 1" class="flex gap-[8px]">
    <div class="relative">
      <ImageAudioVideo class="w-[58px] cursor-pointer" :attachment="{
        file_type: 'image',
        data_url: coverImage,
      }" />
      <div v-if="count" class="absolute bottom-[4px] inset-x-0 mx-auto w-[20px] h-[20px] rounded-full text-[12px] bg-[#00000099] text-[#fff] text-center">
        x{{ count }}
      </div>
    </div>
    <div class="flex-1 flex flex-col justify-between">
      <div>
        <p class="leading-[15px] mb-[12px] text-[12px]">{{ goodsName }}</p>
        <div class="leading-[15px]">
          <img v-if="skuValues[0]?.pic" class="inline-block w-[10px] !h-[10px] rounded-[2px]" :src="skuValues[0]?.pic ?? ''">
          <span v-if="skuValues[0]?.pic && skuValues[1]?.value" class="font-bold text-[12px]"> / </span>
          <span v-if="skuValues[1]?.value" class="font-semibold text-[12px]">{{ skuValues[1]?.value }}</span>
        </div>
      </div>
      <div class="flex justify-end items-center">
        <span v-if="totalActualReturnQty" class="me-[10px] text-[12px]">x{{ totalActualReturnQty }}</span>
        <template v-if="totalAmountTxt">
          <span class="text-[12px]">{{ total ?? t('CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.TOTAL') }}: </span>
          <span class="font-bold ms-[4px] text-[14px]">{{ totalAmountTxt }}</span>
        </template>
      </div>
    </div>
  </div>
  <!-- 多卡片 -->
  <template v-else>
    <div class="flex gap-[10px]">
      <div
        v-for="item in showList"
        :key="item.goodsId"
        class="relative"
      >
        <ImageAudioVideo class="!h-[81px] cursor-pointer" :attachment="{
          file_type: 'image',
          data_url: item.coverImage,
        }" />
        <div v-if="item.count" class="absolute bottom-[4px] inset-x-0 mx-auto w-[20px] h-[20px] rounded-full text-[12px] bg-[#00000099] text-[#fff] text-center">
          x{{ item.count }}
        </div>
      </div>
      <div v-if="reverseItemList.length > 4" class="bg-[#F4F4F4] !h-[81px] aspect-[3/4] flex flex-col items-center justify-center cursor-pointer" @click="handleDetail">
        <IconRightArrowLong class="inline-block mb-[4px]" />
        <p class="text-[12px] mb-0 leading-[14px]">{{ t('CONVERSATION.CARD_MESSAGE.ORDER.VIEW') }}</p>
        <p class="text-[12px] mb-0 leading-[14px]">{{ t('CONVERSATION.CARD_MESSAGE.ORDER.MORE') }}</p>
      </div>
    </div>
    <div class="flex justify-end items-center mt-[10px]">
      <span v-if="totalActualReturnQty" class="me-[10px] text-[12px]">x{{ totalActualReturnQty }}</span>
      <template v-if="totalAmountTxt">
        <span class="text-[12px]">{{ total ?? t('CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.TOTAL') }}: </span>
        <span class="font-bold ms-[4px] text-[14px]">{{ totalAmountTxt }}</span>
      </template>
    </div>
  </template>
  <!-- View 按钮 -->
  <div class="bg-[#000] text-[#fff] font-semibold text-[14px] text-center rounded-[10px] mt-[12px] h-[33px] leading-[33px] cursor-pointer" @click="handleDetail">
    {{ view ?? t('CONVERSATION.CARD_MESSAGE.VIEW_DETAILS') }}
  </div>
</template>

<style scoped>
.text-desc {
  @apply mb-0 leading-[15px] text-[12px];
}
</style>
